// CAPTCHA widget for the public auth forms (Cloudflare Turnstile by
// default). The provider + site key come from the backend, so the bundle
// stays deployment-agnostic and no widget renders when CAPTCHA is disabled.
//
// Ported from the oracle-daemon-support frontend, which runs this in
// production.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../api/client";
import { getTheme } from "../lib/theme";

const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** The server restricts deployments to Cloudflare Turnstile (or no
 *  CAPTCHA). Any other provider string is a misconfiguration this widget
 *  cannot satisfy — see `captchaUnsupported`. */
export interface CaptchaConfig {
  provider: "turnstile" | "disabled" | (string & {});
  site_key: string;
}

/** What a form can ask of the widget after a failed submit. */
export interface CaptchaHandle {
  /** Drop the spent token and show a fresh challenge. A Turnstile token is
   *  single-use: the server consumed it on the failed attempt, so retrying
   *  with it fails until the widget is reset. */
  reset: () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi | undefined> | null = null;
function loadTurnstile(): Promise<TurnstileApi | undefined> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.turnstile) return resolve(window.turnstile);
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => {
      scriptPromise = null; // allow a retry on the next mount
      reject(new Error("turnstile failed to load"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/** Auth-provider config (captcha + google), fetched once per session. */
export function useAuthProviders() {
  return useQuery({
    queryKey: ["auth-providers"],
    queryFn: () =>
      get<{
        google: boolean;
        captcha: CaptchaConfig;
        email_verification_required: boolean;
      }>("/api/auth/providers"),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Whether a form must hold a token before it may submit — i.e. whether
 * <Captcha> will actually render a challenge.
 *
 * Forms MUST gate their submit button on this and not on `provider` alone: a
 * deployment with the provider set but no site key (half-filled .env) renders
 * no widget, so a provider-only check disables the button forever with nothing
 * on screen to satisfy it. Letting the request through instead surfaces the
 * misconfiguration as the backend's 503/400 — still fail-closed, but visible.
 */
export function captchaRequired(config?: CaptchaConfig): boolean {
  return config?.provider === "turnstile" && !!config.site_key;
}

/** A provider the server asks for but this SPA cannot render (anything but
 *  Turnstile). Forms show an explicit error instead of a submit button that
 *  can never succeed. */
export function captchaUnsupported(config?: CaptchaConfig): boolean {
  return !!config && config.provider !== "turnstile" && config.provider !== "disabled" &&
    !!config.provider;
}

/**
 * Renders the challenge and hands tokens to `onToken` (null when the token
 * expires, so the form can re-disable its submit button). Returns nothing
 * when CAPTCHA is disabled server-side. Forms hold a ref and call
 * `reset()` after every failed submit.
 */
export const Captcha = forwardRef<CaptchaHandle, {
  config?: CaptchaConfig;
  onToken: (token: string | null) => void;
}>(function Captcha({ config, onToken }, handle) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);
  const siteKey = config?.site_key ?? "";
  const enabled = captchaRequired(config);

  useImperativeHandle(handle, () => ({
    reset: () => {
      onToken(null);
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetId.current);
        } catch {
          /* widget already gone — the next mount renders a fresh one */
        }
      }
    },
  }), [onToken]);

  useEffect(() => {
    if (!enabled || !ref.current) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !ts || !ref.current) return;
        widgetId.current = ts.render(ref.current, {
          sitekey: siteKey,
          theme: getTheme() === "dark" ? "dark" : "light",
          callback: (token: string) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already gone */
        }
      }
      widgetId.current = null;
    };
    // onToken is a stable setter in practice; re-rendering the widget on
    // every keystroke would reset the challenge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, siteKey]);

  if (captchaUnsupported(config)) {
    return (
      <div className="card p-3 text-sm text-bad border-bad/40 bg-bad/10">
        This server requires a verification challenge ({config?.provider}) that this app cannot
        display. Please contact the site operator.
      </div>
    );
  }
  if (!enabled) return null;
  return (
    <div>
      <div ref={ref} className="min-h-[65px]" />
      {failed && (
        <div className="text-xs text-bad">
          The verification challenge could not be loaded. Disable your ad blocker or try again.
        </div>
      )}
    </div>
  );
});
